import React, { useRef, useState, useEffect } from 'react';
import { customToast } from '../utils/toastUtils';
import { useNavigate } from 'react-router-dom';
import TypingAnimation from '../components/TypingAnimation';
import Premium3DBanner from '../components/Premium3DBanner';
import EnhancedPremium3DBanner from '../components/EnhancedPremium3DBanner';
import CounterAnimation from '../components/CounterAnimation';
import logo from '../assets/ResumeGlobeLogo.svg';
import ibmLogo from '../assets/IBM.svg';
import capgeminiLogo from '../assets/Capgemini.svg';
import hclLogo from '../assets/hcl.svg';
import nttDataLogo from '../assets/NTT_Data.svg';
import cognizantLogo from '../assets/Cognizant.svg';
import tcsLogo from '../assets/tcs.svg';
import infosysLogo from '../assets/Infosys_logo.svg';
import wiproLogo from '../assets/wipro.svg';
import zohoLogo from '../assets/zoho.svg';

function HomePage({
  isLoggedIn,
  onGetStartedClick,
  onFileUploadClick,
  onLoginClick,
  onGenerateCoverLetterClick,
  onTermsClick,
  onRefundClick,
  onPrivacyClick,
  onFaqClick,
  onFeedbackClick,
  onContactUsClick,
  heroRef,
  productRef,
  whyRef,
  atsRef,
  coverLetterRef,
  aboutRef
}) {
  const navigate = useNavigate();
  
  // ATS Score Animation State
  const [atsScore, setAtsScore] = useState(0);
  const [scanLineProgress, setScanLineProgress] = useState(0);

  // Animate ATS score from 0 to 95
  useEffect(() => {
    let currentScore = 0;
    const targetScore = 95;
    const duration = 2000; // 2 seconds
    const steps = 60;
    const increment = targetScore / steps;
    const interval = duration / steps;

    const timer = setInterval(() => {
      currentScore += increment;
      if (currentScore >= targetScore) {
        setAtsScore(targetScore);
        clearInterval(timer);
      } else {
        setAtsScore(Math.floor(currentScore));
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  // Animate scan line progress
  useEffect(() => {
    let progress = 0;
    const duration = 2000; // 2 seconds
    const steps = 60;
    const increment = 95 / steps;
    const interval = duration / steps;

    const timer = setInterval(() => {
      progress += increment;
      if (progress >= 95) {
        setScanLineProgress(95);
        clearInterval(timer);
      } else {
        setScanLineProgress(progress);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section ref={heroRef} className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Create Professional Resumes with 
              <span className="gradient-text"> AI-Powered</span> Tools
            </h1>
            <p className="hero-description">
              Build ATS-friendly resumes that get you hired. Choose from professional templates, 
              optimize for applicant tracking systems, and land your dream job faster.
            </p>
            
            <div className="hero-actions">
              <button 
                className="btn-create-resume"
                onClick={onGetStartedClick}
              >
                Create Resume
              </button>
              <button 
                className="btn-upload-resume"
                onClick={() => onFileUploadClick('resume-upload')}
              >
                Import & Upload Resume
              </button>
            </div>

            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">
                  <CounterAnimation targetNumber="50K" duration={2000} startDelay={300} />+
                </span>
                <span className="stat-label">Resumes Created</span>
              </div>
              <div className="stat">
                <span className="stat-number">
                  <CounterAnimation targetNumber="95%" duration={2000} startDelay={500} />
                </span>
                <span className="stat-label">ATS Pass Rate</span>
              </div>
              <div className="stat">
                <span className="stat-number">
                  <CounterAnimation targetNumber="24/7" duration={1500} startDelay={1100} />
                </span>
                <span className="stat-label">AI Support</span>
              </div>
            </div>
          </div>

          <div className="hero-visual">
            <Premium3DBanner />
          </div>
        </div>
      </section>

      {/* Trusted Companies Section */}
      {import.meta.env.VITE_SHOW_TRUSTED_COMPANIES === 'true' && (
      <section className="trusted-companies-section">
        <div className="container">
          <h3 className="trusted-title">Trusted by Professionals at Top Companies</h3>
          <div className="companies-slider">
            <div className="companies-track">
              <div className="company-slide">
                <img src={ibmLogo} alt="IBM" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={capgeminiLogo} alt="Capgemini" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={hclLogo} alt="HCL" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={nttDataLogo} alt="NTT Data" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={cognizantLogo} alt="Cognizant" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={tcsLogo} alt="TCS" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={infosysLogo} alt="Infosys" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={wiproLogo} alt="Wipro" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={zohoLogo} alt="Zoho" className="company-logo-slide" />
              </div>
              {/* Duplicate for seamless loop */}
              <div className="company-slide">
                <img src={ibmLogo} alt="IBM" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={capgeminiLogo} alt="Capgemini" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={hclLogo} alt="HCL" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={nttDataLogo} alt="NTT Data" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={cognizantLogo} alt="Cognizant" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={tcsLogo} alt="TCS" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={infosysLogo} alt="Infosys" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={wiproLogo} alt="Wipro" className="company-logo-slide" />
              </div>
              <div className="company-slide">
                <img src={zohoLogo} alt="Zoho" className="company-logo-slide" />
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Product Summary Section */}
      <section ref={productRef} className="product-section">
        <div className="container">
          <div className="section-header">
            <h2>Everything You Need to Land Your Dream Job</h2>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <span style={{fontSize: '48px', lineHeight: '1'}}>📄</span>
              </div>
              <h3>Professional Templates</h3>
              <p>Choose from 50+ ATS-friendly templates designed by HR experts</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <span style={{fontSize: '48px', lineHeight: '1'}}>🤖</span>
              </div>
              <h3>AI Content Suggestions</h3>
              <p>Get intelligent suggestions for bullet points and job descriptions</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <span style={{fontSize: '48px', lineHeight: '1'}}>🎯</span>
              </div>
              <h3>ATS Optimization</h3>
              <p>Ensure your resume passes applicant tracking systems with 95% success rate</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <span style={{fontSize: '48px', lineHeight: '1'}}>⚡</span>
              </div>
              <h3>Quick Export</h3>
              <p>Download in PDF, Word, or share with a custom link in seconds</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <span style={{fontSize: '48px', lineHeight: '1'}}>🔐</span>
              </div>
              <h3>Privacy First</h3>
              <p>Your data is encrypted and secure. We never share your information</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="6" width="16" height="12" rx="1.5" stroke="#ffffff" strokeWidth="1.5" fill="rgba(255, 255, 255, 0.2)"/>
                  <path d="M4 8L12 13L20 8" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Cover Letters</h3>
              <p>Create matching cover letters that complement your resume perfectly</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why ResumeGlobe Section */}
      <section ref={whyRef} className="why-section">
        <div className="container">
          <div className="why-content">
            <div className="why-header">
              <h2>Why Choose RESUMEGLOBE?</h2>
              <p className="why-subtitle">
                The smartest choice for students, graduates, and professionals seeking career advancement
              </p>
            </div>

            <div className="why-features-grid">
                <div className="why-feature">
                  <div className="why-icon-modern">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 3.5C14.8 3.4 14.6 3.4 14.4 3.5L12 4.5L9.6 3.5C9.4 3.4 9.2 3.4 9 3.5L3 7V9H21ZM12 7L15 8.5L12 10L9 8.5L12 7ZM3 19V11H5V19H7V11H9V19H11V11H13V19H15V11H17V19H19V11H21V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19Z" fill="currentColor"/>
                    </svg>
                  </div>
                  <div>
                    <h4>Affordable Pricing</h4>
                    <p>Up to 50% cheaper than other platforms with flexible daily, weekly, and yearly plans</p>
                  </div>
                </div>

                <div className="why-feature">
                  <div className="why-icon-modern">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
                    </svg>
                  </div>
                  <div>
                    <h4>AI-Powered Enhancements</h4>
                    <p>Industry-specific suggestions and optimization to boost your success rates</p>
                  </div>
                </div>

                <div className="why-feature">
                  <div className="why-icon-modern">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                    </svg>
                  </div>
                  <div>
                    <h4>Free Cover Letter Included</h4>
                    <p>Get a professionally written cover letter included with every plan</p>
                  </div>
                </div>

                <div className="why-feature">
                  <div className="why-icon-modern">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                    </svg>
                  </div>
                  <div>
                    <h4>ATS Optimization</h4>
                    <p>Beat the bots with AI-suggested keywords and ATS-compatible templates</p>
                  </div>
                </div>

                <div className="why-feature">
                  <div className="why-icon-modern">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L13.09 8.26L22 9L13.09 15.74L12 22L10.91 15.74L2 9L10.91 8.26L12 2Z" fill="currentColor"/>
                    </svg>
                  </div>
                  <div>
                    <h4>Career Boosting Guidance</h4>
                    <p>MCQ test practice, skill recommendations, and course suggestions</p>
                  </div>
                </div>

                <div className="why-feature">
                  <div className="why-icon-modern">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 4V2C7 1.45 7.45 1 8 1H16C16.55 1 17 1.45 17 2V4H20C20.55 4 21 4.45 21 5S20.55 6 20 6H19V19C19 20.1 18.1 21 17 21H7C5.9 21 5 20.1 5 19V6H4C3.45 6 3 5.55 3 5S3.45 4 4 4H7ZM9 3V4H15V3H9ZM7 6V19H17V6H7Z" fill="currentColor"/>
                    </svg>
                  </div>
                  <div>
                    <h4>Special Offers</h4>
                    <p>Seasonal discounts of 5–15% and user-friendly interface for quick creation</p>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* ATS Score Section */}
      <section ref={atsRef} className="ats-section">
        <div className="container">
          <div className="ats-content">
            <div className="ats-info">
              <h2>What is ATS & Why It Matters</h2>
              <p className="ats-subtitle">
                The Applicant Tracking System (ATS) acts as a digital gatekeeper, scanning and filtering 
                resumes before a recruiter sees them. RESUMEGLOBE bridges this gap.
              </p>

              <div className="ats-benefits">
                <div className="ats-benefit">
                  <span className="benefit-number">01</span>
                  <div>
                    <h4>Digital Gatekeeper</h4>
                    <p>If your resume isn't optimized with the right keywords and format, it may never reach human eyes</p>
                  </div>
                </div>

                <div className="ats-benefit">
                  <span className="benefit-number">02</span>
                  <div>
                    <h4>Strategic Optimization</h4>
                    <p>We help craft resumes that are visually appealing and strategically tailored to pass ATS filters</p>
                  </div>
                </div>

                <div className="ats-benefit">
                  <span className="benefit-number">03</span>
                  <div>
                    <h4>Free ATS Scoring</h4>
                    <p>Scan your existing resume and get instant ATS compatibility feedback at no cost</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="ats-visual">
              <div className="ats-scanner">
                {/* Diagonal Scanner Animation */}
                <div className="scanner-beam"></div>
                
                {/* Laser Scanner Line */}
                <div className="laser-scanner">
                  <div className="laser-line"></div>
                  <div className="laser-glow"></div>
                </div>
                
                <div className="scanner-header">
                  <span>ATS Scanner</span>
                  <span className="scanner-score">{atsScore}%</span>
                </div>
                <div className="scanner-content">
                  <div className="scan-line active" style={{ width: `${scanLineProgress}%` }}></div>
                  <div className="scan-line"></div>
                  <div className="scan-line active" style={{ width: `${scanLineProgress * 0.85}%` }}></div>
                  <div className="scan-line active" style={{ width: `${scanLineProgress * 0.92}%` }}></div>
                  <div className="scan-line"></div>
                  <div className="scan-result" style={{ opacity: atsScore >= 95 ? 1 : 0 }}>
                    ✅ Excellent ATS Compatibility
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Centered Button Below */}
          <div className="ats-button-container">
            <button 
              className="btn-ats-modern btn-pulse"
              onClick={() => onFileUploadClick('ats-check')}
            >
              Check My ATS Score
            </button>
          </div>
        </div>
      </section>

      {/* Cover Letter Section */}
      <section ref={coverLetterRef} className="cover-letter-section">
        <div className="container">
          <div className="cover-letter-content">
            <div className="section-header">
              <h2>We Care About Your Success!</h2>
              <h3 className="section-subtitle">Create Your Personalized Cover Letter!</h3>
            </div>

            <div className="cover-letter-hero">
              <p className="hero-text">
                With ResumeGlobe, you can create a professional, personalized cover letter using our AI-powered tool. 
                Experience our premium templates and see a <strong>live preview</strong> as you provide your inputs — 
                all tailored to highlight your skills and make you stand out. <span className="highlight">No extra fees, just an extra boost!</span>
              </p>
            </div>

            <div className="why-matters-section">
              <h3 className="section-title">Why a cover letter matters:</h3>
              <p className="matters-description">
                A cover letter is a brief, personalized letter that introduces your application, highlights your key skills, 
                and explains why you're the right fit for the job. It goes beyond your resume, creating a personal connection 
                with the hiring manager — especially when paired with our custom-designed templates.
              </p>
            </div>

            <div className="cover-letter-features">
              <div className="cover-letter-feature">
                <div className="feature-icon">🤖</div>
                <h3>AI-Powered Tool</h3>
                <p>Smart AI technology creates personalized, professional cover letters tailored to your experience.</p>
              </div>

              <div className="cover-letter-feature">
                <div className="feature-icon">👁️</div>
                <h3>Live Preview</h3>
                <p>See your cover letter come to life in real-time as you input your information with premium templates.</p>
              </div>

              <div className="cover-letter-feature">
                <div className="feature-icon">✨</div>
                <h3>Stand Out</h3>
                <p>Custom-designed templates that make you stand out and create a personal connection with hiring managers.</p>
              </div>
            </div>

            {!isLoggedIn && (
              <div className="login-prompt">
                <p className="prompt-text">
                  To fully access and save your cover letter,{' '}
                  <span 
                    className="login-link" 
                    onClick={onLoginClick}
                  >
                    log in or sign up
                  </span>{' '}
                  on ResumeGlobe for a seamless user experience!
                </p>
              </div>
            )}

            <div className="cover-letter-cta">
              <button 
                className="btn-cover-letter"
                onClick={onGenerateCoverLetterClick}
              >
                Create Your Cover Letter Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section ref={aboutRef} className="about-section">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>About RESUMEGLOBE</h2>
              <p className="about-subtitle">
                The next-generation AI-powered resume and cover letter building platform designed for today's competitive market.
              </p>

              <div className="about-story">
                <p>
                  RESUMEGLOBE combines cutting-edge AI technology with user-friendly design to create faster, 
                  better resumes that beat Applicant Tracking Systems (ATS) while staying budget-friendly. 
                  We're specifically designed for students and graduates who need to create professional resumes quickly and easily.
                </p>
                
                <p>
                  Our advanced AI engine doesn't just format your resume—it analyzes job descriptions, identifies 
                  essential keywords, and fine-tunes your content for maximum ATS compatibility. With flexible 
                  pricing up to 50% cheaper than competitors and comprehensive career guidance tools, we're making 
                  professional resume building accessible to everyone.
                </p>
              </div>

              <div className="about-stats">
                <div className="stat">
                  <div className="stat-number">50%</div>
                  <div className="stat-label">Cheaper Pricing</div>
                </div>
                <div className="stat">
                  <div className="stat-number">FREE</div>
                  <div className="stat-label">ATS Score Check</div>
                </div>
                <div className="stat">
                  <div className="stat-number">15%</div>
                  <div className="stat-label">Seasonal Discounts</div>
                </div>
              </div>
            </div>

            <div className="about-visual">
              <div className="about-logo-container">
                <img src={logo} alt="ResumeGlobe" className="about-logo-img" />
                <p className="about-logo-tagline">Where Dreams Resume</p>
              </div>
              <div className="about-card">
                <h3>Our Mission</h3>
                <p>To bridge the gap between talented professionals and their ideal opportunities using AI technology that's both powerful and affordable.</p>
              </div>
              
              {/* Animated Mission Icon */}
              <div className="mission-icon-container">
                <div className="mission-icon">
                  <svg className="mission-svg" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                    {/* Outer rotating ring */}
                    <circle className="mission-ring" cx="100" cy="100" r="80" fill="none" stroke="url(#gradient1)" strokeWidth="3" opacity="0.6"/>
                    
                    {/* Middle pulsing ring */}
                    <circle className="mission-pulse" cx="100" cy="100" r="60" fill="none" stroke="url(#gradient2)" strokeWidth="2" opacity="0.4"/>
                    
                    {/* Center target icon */}
                    <circle cx="100" cy="100" r="40" fill="url(#gradient3)" opacity="0.2"/>
                    <path className="mission-target" d="M100 70 L100 55 M100 145 L100 130 M70 100 L55 100 M145 100 L130 100" stroke="#1A8063" strokeWidth="4" strokeLinecap="round"/>
                    <circle cx="100" cy="100" r="15" fill="#1A8063"/>
                    <circle cx="100" cy="100" r="8" fill="white"/>
                    
                    {/* Gradient definitions */}
                    <defs>
                      <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style={{stopColor: '#1A8063', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#16a085', stopOpacity: 1}} />
                      </linearGradient>
                      <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style={{stopColor: '#16a085', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#1A8063', stopOpacity: 1}} />
                      </linearGradient>
                      <radialGradient id="gradient3">
                        <stop offset="0%" style={{stopColor: '#1A8063', stopOpacity: 0.3}} />
                        <stop offset="100%" style={{stopColor: '#16a085', stopOpacity: 0.1}} />
                      </radialGradient>
                    </defs>
                  </svg>
                  <div className="mission-label">Target Excellence</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <div className="footer-logo-wrapper">
                  <img src={logo} alt="ResumeGlobe" className="footer-logo-img" />
                  <p className="footer-logo-tagline">Where Dreams Resume</p>
                </div>
              </div>
              <p className="footer-description">
                AI-powered resume and cover letter builder helping professionals land their dream jobs.
              </p>
            </div>

            <div className="footer-section">
              <h4>Product</h4>
              <ul className="footer-links">
                <li><a href="#" onClick={(e) => { e.preventDefault(); navigate('/templates'); }}>Resume Builder</a></li>
                <li><a href="#" onClick={(e) => { e.preventDefault(); coverLetterRef.current?.scrollIntoView({ behavior: 'smooth' }); }}>Cover Letter</a></li>
                <li><a href="#" onClick={(e) => { e.preventDefault(); atsRef.current?.scrollIntoView({ behavior: 'smooth' }); }}>ATS Checker</a></li>
                <li><a href="#" onClick={(e) => { e.preventDefault(); onFileUploadClick('resume'); }}>Upload Resume</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Company</h4>
              <ul className="footer-links">
                <li><a href="#" className="blog-link" onClick={(e) => { e.preventDefault(); navigate('/blog'); }}>Blog</a></li>
                <li><a href="#" onClick={(e) => { e.preventDefault(); onPrivacyClick(); }}>Privacy Policy</a></li>
                <li><a href="#" onClick={(e) => { e.preventDefault(); onTermsClick(); }}>Terms and Conditions</a></li>
                <li><a href="#" onClick={(e) => { e.preventDefault(); onRefundClick(); }}>Refund Policy</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Support</h4>
              <ul className="footer-links">
                <li><a href="#" onClick={(e) => { e.preventDefault(); onFaqClick(); }}>FAQ</a></li>
                <li><a href="#" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }}>Pricing Plans</a></li>
                <li><a href="/customer-support">Customer Support</a></li>
                <li><a href="#" onClick={(e) => { e.preventDefault(); onContactUsClick(); }}>Contact Us</a></li>
                <li><a href="#" onClick={(e) => { e.preventDefault(); onFeedbackClick(); }}>Feedback</a></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-bottom-content">
              <p>&copy; 2026 ResumeGlobe. All rights reserved.</p>
              <div className="footer-social">
                <span>Follow us:</span>
                <a href="https://www.linkedin.com/in/resumeglobe/" target="_blank" rel="noopener noreferrer" className="social-link">LinkedIn</a>
                <a href="https://www.instagram.com/resumeglobe_?igsh=MWoyMTduNThteWE5cA==" target="_blank" rel="noopener noreferrer" className="social-link">Instagram</a>
                <a href="https://www.facebook.com/profile.php?id=61591953971848" target="_blank" rel="noopener noreferrer" className="social-link">Facebook</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

export default HomePage;
